import React, { useEffect } from 'react'
import { FormProvider, Path, useFormContext, useWatch } from 'react-hook-form'
import { Card as MuiCard, CardContent, Typography, Button, Stack } from '@mui/material'
import Grid from '@mui/material/Grid'
import { styled } from '@mui/material/styles'
import { spacing } from '@mui/system'
import * as datefns from 'date-fns'

import { RegisteredCheckBox } from '../../../components/fields/CheckBox'
import {
    RegisteredDecimalField,
    RegisteredIntegerField,
    RegisteredNumericField,
} from '../../../components/fields/NumericField'
import { RegisteredDatePicker } from '../../../components/fields/DatePicker'
import { WarrantSubscriptionPeriod } from '../../../types/WarrantEntity'
import { FormWarrantEntity } from '../types/FormWarrantEntity'

const Card = styled(MuiCard)(spacing)

const FormFieldset = ({ children, title }: { children: React.ReactNode; title: React.ReactNode }) => (
    <fieldset style={{ borderRadius: 2, borderStyle: 'dashed', padding: 10, marginBottom: 10 }}>
        <legend>{title}</legend>
        {children}
    </fieldset>
)

type WarrantSubPeriodProps = {
    handleRemoveSubPeriod: (sp: WarrantSubscriptionPeriod & { tempId: string }) => void
    subPeriodIndex: number
    subPeriod: WarrantSubscriptionPeriod & { tempId: string }
}

function WarrantSubPeriod({ subPeriodIndex, subPeriod, handleRemoveSubPeriod }: WarrantSubPeriodProps) {
    const formValues = useWatch<FormWarrantEntity>()
    const formMethods = useFormContext<FormWarrantEntity>()
    const feeValue = formMethods.watch('fees')
    const formScope = 'subscriptionPeriods'
    const formNamePrefix = `${formScope}.${subPeriodIndex}` as Path<FormWarrantEntity>
    const guarantorsValue = formMethods.watch(`${formNamePrefix}.guarantors` as Path<FormWarrantEntity>)
    const earliestSourcePublicationPublishedDate = formValues.origins?.reduce(
        (value, origin) => {
            const opd = new Date(origin.publicationDate ?? 0)
            if (value === null) return opd

            return value < opd ? value : opd
        },
        null as Date | null,
    )
    const hasFeeUnits = feeValue?.some((f) => !!f.feeUnits)
    const hasGuarantors = !!guarantorsValue

    useEffect(() => {
        if (!hasFeeUnits) {
            formMethods.setValue(`${formNamePrefix}.guarantorRemunerationShares` as Path<FormWarrantEntity>, null)
        }

        if (!hasGuarantors) {
            formMethods.setValue(`${formNamePrefix}.guarantorRemunerationShares` as Path<FormWarrantEntity>, null)
            formMethods.setValue(`${formNamePrefix}.guarantorAllocation` as Path<FormWarrantEntity>, null)
        }
    }, [hasFeeUnits, hasGuarantors, formMethods.setValue, formNamePrefix])

    const subStartDateMinDate = datefns.max([
        new Date(formValues[formScope]?.[subPeriodIndex]?.vwapPeriodEnd || 0),
        datefns.addDays(new Date(earliestSourcePublicationPublishedDate || 0), 1),
    ])

    const formSubStartDate = formValues[formScope]?.[subPeriodIndex]?.subStartDate || null

    const subEndDateMinDate = datefns.addDays(new Date(formSubStartDate || 0), 1)
    const referenceDate = formSubStartDate ? subEndDateMinDate : new Date()

    const validateSubPrice = () => (_value: string) => {
        if (parseFloat(_value) === 0) return false

        return true
    }

    return (
        <FormProvider {...formMethods}>
            <Card mb={6}>
                <CardContent>
                    <Stack
                        direction='row'
                        spacing={2}
                        sx={{
                            alignItems: 'center',
                            my: 2,
                        }}
                    >
                        <Typography
                            variant='h5'
                            gutterBottom
                            sx={{
                                display: 'block',
                            }}
                        >
                            {subPeriod?.id && `Teckningsperiod ${subPeriodIndex + 1}`}
                            {!subPeriod?.id && `Ny teckningsperiod ${subPeriodIndex + 1}`}
                        </Typography>
                        <Button variant='outlined' size='small' onClick={() => handleRemoveSubPeriod(subPeriod)}>
                            Ta bort
                        </Button>
                    </Stack>
                    <FormFieldset title='Tidpunkt'>
                        <Grid columns={10} container spacing={2}>
                            <Grid size={{ xs: 10, md: 2 }}>
                                <RegisteredDatePicker
                                    name={`${formNamePrefix}.subStartDate`}
                                    label='Startdatum'
                                    minDate={subStartDateMinDate}
                                />
                            </Grid>
                            <Grid size={{ xs: 10, md: 2 }}>
                                <RegisteredDatePicker
                                    name={`${formNamePrefix}.subEndDate`}
                                    label='Slutdatum'
                                    minDate={subEndDateMinDate}
                                    referenceDate={referenceDate}
                                />
                            </Grid>
                            <Grid size={{ xs: 10, md: 2 }}>
                                <RegisteredIntegerField
                                    name={`${formNamePrefix}.warrantsRequired`}
                                    label='Antal TO som krävs'
                                    fullWidth
                                />
                            </Grid>
                            <Grid
                                size={{ xs: 10, md: 1 }}
                                sx={{
                                    alignContent: 'center',
                                    textAlign: 'center',
                                }}
                            >
                                {'=>'}
                            </Grid>
                            <Grid size={{ xs: 10, md: 2 }}>
                                <RegisteredIntegerField
                                    name={`${formNamePrefix}.sharesReceived`}
                                    label='Antal aktier'
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </FormFieldset>
                    <FormFieldset title='VWAP'>
                        <Grid columns={10} container spacing={2}>
                            <Grid size={{ xs: 10, md: 2 }}>
                                <RegisteredCheckBox name={`${formNamePrefix}.isVwap`} label='VWAP' />
                            </Grid>
                            {!!subPeriod.isVwap && (
                                <Grid size={{ xs: 10, md: 2 }}>
                                    <RegisteredNumericField
                                        name={`${formNamePrefix}.vwapDiscount`}
                                        label='Rabatt till VWAP'
                                        percentage
                                        fullWidth
                                    />
                                </Grid>
                            )}
                            {!!subPeriod.isVwap && (
                                <Grid size={{ xs: 10, md: 2 }}>
                                    <RegisteredIntegerField
                                        name={`${formNamePrefix}.vwapDays`}
                                        label='Dagar för VWAP'
                                        fullWidth
                                    />
                                </Grid>
                            )}
                            {!!subPeriod.isVwap && (
                                <Grid size={{ xs: 10, md: 3 }}>
                                    <RegisteredDatePicker
                                        name={`${formNamePrefix}.vwapPeriodEnd`}
                                        label='Slutdatum för VWAP'
                                        maxDate={
                                            formValues[formScope]?.[subPeriodIndex].subStartDate
                                                ? new Date(formValues[formScope][subPeriodIndex].subStartDate)
                                                : undefined
                                        }
                                        minDate={datefns.addDays(earliestSourcePublicationPublishedDate ?? 0, 1)}
                                        sx={{ width: '100%' }}
                                    />
                                </Grid>
                            )}
                        </Grid>
                        <Grid
                            columns={10}
                            container
                            spacing={2}
                            sx={{
                                marginTop: 2,
                            }}
                        >
                            {!!subPeriod.isVwap && (
                                <Grid size={{ xs: 10, md: 2 }}>
                                    <RegisteredCheckBox
                                        name={`${formNamePrefix}.priceMinQuota`}
                                        label='Kvotvärde'
                                        disabled={
                                            !subPeriod.isVwap ||
                                            (subPeriod.priceMin != null && ('' + subPeriod.priceMin).length > 0)
                                        }
                                    />
                                </Grid>
                            )}
                            {!!subPeriod.isVwap && (
                                <Grid size={{ xs: 10, md: 2 }}>
                                    <RegisteredDecimalField
                                        name={`${formNamePrefix}.priceMin`}
                                        label='Minimum teckningskurs'
                                        fullWidth
                                        disabled={!subPeriod.isVwap || !!subPeriod.priceMinQuota}
                                    />
                                </Grid>
                            )}
                            {!!subPeriod.isVwap && (
                                <Grid size={{ xs: 10, md: 2 }}>
                                    <RegisteredCheckBox
                                        name={`${formNamePrefix}.noPriceMax`}
                                        label='Tak saknas'
                                        disabled={
                                            !subPeriod.isVwap ||
                                            (subPeriod.priceMax != null && ('' + subPeriod.priceMax).length > 0)
                                        }
                                    />
                                </Grid>
                            )}
                            {!!subPeriod.isVwap && (
                                <Grid size={{ xs: 10, md: 3 }}>
                                    <RegisteredDecimalField
                                        name={`${formNamePrefix}.priceMax`}
                                        label='Maximal teckningskurs'
                                        disabled={!subPeriod.isVwap || !!subPeriod.noPriceMax}
                                        fullWidth
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </FormFieldset>

                    <FormFieldset title='Villkor'>
                        <Grid columns={10} container spacing={2}>
                            <Grid size={{ xs: 10, md: 3 }}>
                                <RegisteredDecimalField
                                    name={`${formNamePrefix}.warrantSubPrice`}
                                    label='Teckningskurs'
                                    fullWidth
                                    validation={{ validate: validateSubPrice() }}
                                />
                            </Grid>
                            <Grid size={{ xs: 10, md: 3 }}>
                                <RegisteredNumericField
                                    name={`${formNamePrefix}.commitments`}
                                    label='Teckningsåtagande (%)'
                                    fullWidth
                                    percentage
                                />
                            </Grid>
                            <Grid size={{ xs: 10, md: 3 }}>
                                <RegisteredNumericField
                                    name={`${formNamePrefix}.guarantors`}
                                    label='Garantiåtagande (%)'
                                    fullWidth
                                    percentage
                                />
                            </Grid>
                        </Grid>
                    </FormFieldset>

                    <FormFieldset title='Utfall'>
                        <Grid columns={10} container spacing={2}>
                            <Grid size={{ xs: 10, md: 3 }}>
                                <RegisteredIntegerField
                                    name={`${formNamePrefix}.sharesSubscribed`}
                                    label='Antal tecknade aktier (ex. garanter)'
                                    fullWidth
                                />
                            </Grid>
                            {hasGuarantors && (
                                <Grid size={{ xs: 10, md: 3 }}>
                                    <RegisteredIntegerField
                                        name={`${formNamePrefix}.guarantorAllocation`}
                                        label='Tilldelning garanter antal aktier'
                                        fullWidth
                                    />
                                </Grid>
                            )}
                            {hasGuarantors && hasFeeUnits && (
                                <Grid size={{ xs: 10, md: 3 }}>
                                    <RegisteredIntegerField
                                        name={`${formNamePrefix}.guarantorRemunerationShares`}
                                        label='Garantiersättning antal aktier'
                                        fullWidth
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </FormFieldset>
                </CardContent>
            </Card>
        </FormProvider>
    )
}

export default WarrantSubPeriod
